import { useCallback, useEffect, useState } from 'react';

import { RegimeChave } from './useFechamentos';

// ============================================================================
// Tipos e defaults
// ============================================================================

export const REGIMES_DISPONIVEIS: { chave: RegimeChave; label: string }[] = [
  { chave: 'lr', label: 'Lucro Real' },
  { chave: 'lp', label: 'Lucro Presumido' },
  { chave: 'sn', label: 'Simples Nacional' },
  { chave: 'mei', label: 'MEI' },
  { chave: 'outros', label: 'Outros (Domésticas, Imune/Isenta)' },
];

const TODAS_CHAVES: RegimeChave[] = REGIMES_DISPONIVEIS.map(r => r.chave);

// MEI e Outros desabilitados por padrao — sao regimes "ruidosos" para
// fechamento contabil (MEI tem obrigacao anual, Domesticas nao tem fechamento
// no Dominio). Selecionar so LR/LP/SN da uma visao mais util pro contador.
export const REGIMES_INCLUIDOS_DEFAULT: RegimeChave[] = ['lr', 'lp', 'sn'];

const STORAGE_KEY = 'fechamentos:regimes_incluidos';

// ============================================================================
// Helper para enviar como querystring
// ============================================================================

export const regimesIncluidosToParams = (regimes: RegimeChave[]): Record<string, string> => {
  // Lista completa — omite o param pra nao poluir a query (backend trata
  // ausente como "considerar todos").
  if (regimes.length === TODAS_CHAVES.length) return {};
  // Lista vazia: usuario explicitamente desmarcou tudo. Enviar string vazia
  // ou omitir faria o backend cair em "considerar todos" (truthy check em
  // `if params.regimes_incluidos:`), gerando KPIs do universo completo
  // enquanto a secao "Por regime" mostra "Nenhum regime selecionado".
  // Sentinel "__none__" cai na intersecao vazia com a whitelist do backend
  // -> WHERE FALSE -> 0 resultados, mantendo a tela coerente.
  if (regimes.length === 0) return { regimes_incluidos: '__none__' };
  return { regimes_incluidos: regimes.join(',') };
};

// ============================================================================
// Hook (state + persistencia em localStorage)
// ============================================================================

// Ordem canonica (REGIMES_DISPONIVEIS) deduplicada — filtrar por includes
// remove duplicatas e ordena num passo so. Usado tanto no carregamento
// quanto nos setters pra que a query string seja estavel (evita cache miss
// no react-query por reordenacao) e pra que dois ticks com o mesmo
// conjunto de chaves nao gerem listas diferentes.
const toCanonico = (regimes: RegimeChave[]): RegimeChave[] =>
  TODAS_CHAVES.filter(c => regimes.includes(c));

const loadFromStorage = (): RegimeChave[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return REGIMES_INCLUIDOS_DEFAULT;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return REGIMES_INCLUIDOS_DEFAULT;
    const canonico = toCanonico(parsed);
    // Distingue "vazio explicito" (usuario desmarcou tudo) de "corrompido"
    // (storage tinha valores, mas todos sao invalidos) — corrupto cai no
    // default em vez de abrir a tela com "Nenhum regime selecionado".
    if (canonico.length === 0 && parsed.length > 0) {
      return REGIMES_INCLUIDOS_DEFAULT;
    }
    return canonico;
  } catch {
    return REGIMES_INCLUIDOS_DEFAULT;
  }
};

export const useRegimesIncluidos = () => {
  const [regimes, setRegimesState] = useState<RegimeChave[]>(loadFromStorage);

  // Persiste em localStorage como efeito do estado. Mantido fora dos
  // setters pra que o updater funcional do `toggle` fique puro
  // (recomendacao do React: side effects nao devem rodar dentro do
  // updater porque em StrictMode ele e invocado 2x). Idempotente — o
  // primeiro write logo apos a montagem so re-grava o que veio do
  // localStorage.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(regimes));
    } catch {
      // localStorage indisponivel/cheio — segue usando o estado em memoria
    }
  }, [regimes]);

  const setRegimes = useCallback((next: RegimeChave[]) => {
    setRegimesState(toCanonico(next));
  }, []);

  const toggle = useCallback((chave: RegimeChave) => {
    // Setter funcional evita stale closure em cliques rapidos / batches
    // do React 18 — sem isso, dois toggles seguidos no mesmo render
    // poderiam ler `regimes` desatualizado e perder uma das atualizacoes.
    setRegimesState(curr => {
      const next = curr.includes(chave) ? curr.filter(c => c !== chave) : [...curr, chave];
      return toCanonico(next);
    });
  }, []);

  const reset = useCallback(() => {
    setRegimes(REGIMES_INCLUIDOS_DEFAULT);
  }, [setRegimes]);

  return { regimes, setRegimes, toggle, reset };
};
